import schema from "./schema.json";
import type { PlanLesson, PlanUnit, UnitAbout, UnitStandard } from "../home";

// WHAT A UNIT AND A LESSON ARE, read from the schema rather than written out again.
//
// `schema.json` is a byte-identical copy of `backend/writing/serving/content_schema.json`, which
// is canonical; a backend test fails if they drift. The API's allowlist is derived from the same
// file, so a field added there reaches the client and appears in the default view with nothing
// else changed. That property is the whole reason this layer exists.
//
// WHAT THIS FILE IS NOT. It never decides how anything looks. A field says what it IS — its type,
// its label, one sentence of meaning — and the view layer decides what to draw. The moment a
// `className` or a `variant` appears in here, the content model has started describing screens
// and the next module will not be able to reuse it.

export type EntityType = "unit" | "lesson";

/** The types a field may have. The default producer maps each one to a component, so adding a
 *  type here means teaching `produce.ts` what to draw for it. */
export type FieldType = "text" | "prose" | "number" | "text_list" | "standards" | "blocks"
                      | "resources" | "handle";

export interface ContentField {
  name: string;
  type: FieldType;
  label: string;
  /** One sentence saying what the field means. Read by people, and by the model that will one
   *  day choose a component for it. */
  semantic: string;
}

/** A link the module carries, and who it is for.
 *
 *  `kind` is the part a flat list of links throws away: a scoring guide and a student handout are
 *  both URLs and are not the same thing, and a page that showed a teacher's tracker among the
 *  student materials would be showing the cohort something it should not have. */
export interface ContentResource {
  title: string;
  url: string;
  kind: "text" | "student" | "teacher";
}

export type BlockType = "paragraph" | "task_prompt" | "source_reference" | "organizer"
                      | "rubric_row_ref" | "response_area" | "teacher_note";

/** One block of handout content. Narrative stays narrative; structure stays queryable.
 *
 *  Typed rather than HTML because the same content has three audiences that want different
 *  things from it: a teacher reads it, a renderer lays it out, and the scorer needs to know which
 *  part is the PROMPT. An HTML string answers only the second. */
export type Block =
  | { type: "paragraph"; text: string }
  | { type: "task_prompt"; text: string }
  | { type: "source_reference"; title: string; citation?: string }
  | { type: "organizer"; title?: string; slots?: string[] }
  | { type: "rubric_row_ref"; trait_id: string }
  | { type: "response_area"; label?: string; expected?: string }
  | { type: "teacher_note"; text: string };

interface SchemaFile {
  schemaVersion: number;
  entities: Record<string, { label: string; semantic: string; fields: ContentField[] }>;
  blockTypes: { name: BlockType; semantic: string; props: Record<string, string> }[];
}

const SCHEMA = schema as unknown as SchemaFile;

export const SCHEMA_VERSION = SCHEMA.schemaVersion;

export function fields(entity: EntityType): ContentField[] {
  return SCHEMA.entities[entity].fields;
}

export function field(entity: EntityType, name: string): ContentField | undefined {
  return fields(entity).find((f) => f.name === name);
}

export function blockTypes(): BlockType[] {
  return SCHEMA.blockTypes.map((b) => b.name);
}

/** What the renderer is handed: one entity, its values by field name, and its blocks.
 *
 *  Values are keyed by the SCHEMA's names, so a binding is a path into a shape the schema
 *  describes and the validator can check a path without an instance in hand. */
export interface Content {
  entityType: EntityType;
  id: string | null;
  title: string | null;
  values: Record<string, unknown>;
  blocks: Block[];
  /** Where this sits, for the screens that say so. Not part of the entity's own description —
   *  a lesson's unit is a fact about the plan, not about the lesson. */
  context?: { courseName?: string; unitTitle?: string; lessonCount?: number };
}

const EMPTY = (v: unknown) => v === null || v === undefined || v === ""
  || (Array.isArray(v) && v.length === 0);

/** Values by field name, and the handout as blocks.
 *
 *  BLOCKS ARE THE HANDOUT, and only the handout. It is tempting to make every prose field a block
 *  — a teaching task is a `task_prompt`, after all — but LDC NAMES the teaching task, and a named
 *  thing that becomes an anonymous item in a list has lost the part that made it findable. The
 *  blocks are for content that arrives as a sequence and has no name of its own: the prompt as
 *  handed out, the sources beside it, the organiser, the space to write.
 *
 *  A row with no `blocks` has no handout. That is not the same as having nothing — its teaching
 *  task and its texts are still its own fields — and the screens say so differently.
 */
function read(entity: EntityType, about: UnitAbout | undefined,
              id: string | null, title: string | null, context?: Content["context"]): Content {
  const src = (about ?? {}) as Record<string, unknown>;
  const values: Record<string, unknown> = {};

  for (const f of fields(entity)) {
    const v = src[f.name];
    if (EMPTY(v)) continue;
    if (f.name === "blocks") continue;          // read below, as blocks
    values[f.name] = v;
  }

  const blocks = (Array.isArray(src.blocks) ? src.blocks : []) as Block[];
  return { entityType: entity, id, title, values, blocks, context };
}

export function readUnit(unit: PlanUnit, context?: Content["context"]): Content {
  return read("unit", unit.about, unit.unit_id, unit.title,
              { ...context, lessonCount: unit.lessons.length });
}

export function readLesson(lesson: PlanLesson, context?: Content["context"]): Content {
  return read("lesson", lesson.about, lesson.lesson_id, lesson.title, context);
}

/** Read one value out of content by path: `unit.standards`, `lesson.title`, `blocks`.
 *
 *  The leading segment may be the entity's own name — `lesson.skill` reads better in a spec than
 *  `values.skill` — or omitted. Unknown paths return undefined rather than throwing: a field a
 *  unit does not carry is the ordinary case, and the spec's `$when` is what decides whether that
 *  matters. */
export function valueAt(content: Content, path: string): unknown {
  const parts = path.split(".");
  if (parts[0] === content.entityType) parts.shift();
  if (parts.length === 0) return undefined;
  const head = parts.shift() as string;
  let cursor: unknown =
      head === "title" ? content.title
    : head === "id" ? content.id
    : head === "blocks" ? content.blocks
    : head === "context" ? content.context
    : content.values[head];
  for (const part of parts) {
    if (cursor === null || cursor === undefined) return undefined;
    cursor = (cursor as Record<string, unknown>)[part];
  }
  return cursor;
}

/** Does a path name something the SCHEMA knows about? Used by the validator, which must be able
 *  to reject a misspelled binding without an instance in hand — an instance that happens to be
 *  missing a field proves nothing about whether the field exists. */
export function pathIsKnown(entity: EntityType, path: string): boolean {
  const parts = path.split(".");
  if (parts[0] === entity) parts.shift();
  const head = parts[0];
  if (!head) return false;
  if (["title", "id", "blocks", "context"].includes(head)) return true;
  return fields(entity).some((f) => f.name === head);
}

export type { UnitStandard };
